import * as React from "react"
import { MachineContext } from "../fsm/useMachine"
import { TimedPopup } from "../popups/Timed"
import Controls from "../control_bar/ControlPanel"
import { Suspense } from "react"
import NewScreen from "../screens/NewScreen"
import ScreenSelector from "../screens/ScreenSelector"
import { FlowWindow } from "../utils/globals"
import { useCookies } from "../utils/useCookies"
import Tour from "./tutorial"
import _throttle from "lodash.throttle"
import _debounce from "lodash.debounce"
import { isProd, calculateAspectRatioFit, determineOffsetAndRatios } from "../utils/getInfo"
import log from "loglevel"
import { FlowCanvas } from "../view_2/Screen"
import { updateListener } from "../view_2/update-listener"
import { EditorCoordinatorProvider } from "../text_editor/editorCoordinator"
import { mountListeners } from "./listeners"
import { MacAppPopup } from "../marketing/MacAppPopup";
import Popup from "reactjs-popup"
import { EditorThemePicker } from "../ai/EditorThemePicker"
import { useAIRecompile } from "../ai/hooks/useAIRecompile"

declare let window: FlowWindow;


export const Canvas = () => (
    <EditorCoordinatorProvider>
        <InternalCanvas />
    </EditorCoordinatorProvider>
);

!isProd() && log.enableAll()

/**
 * Represents the entire canvas of the editor.
 */
const InternalCanvas = () => {
    const [current,] = React.useContext(MachineContext)
    const stage_w = current.context.flow.viewSize.width
    const stage_h = current.context.flow.viewSize.height

    // Initial calculation using a rough estimate, will be updated after mount
    const [stageBounds, setStageBounds] = React.useState(() => {
        // Use rough estimates for initial render before elements are measured
        const leftOffset = 80;
        const topOffset = 140;
        return calculateAspectRatioFit(
            stage_w,
            stage_h,
            window.innerWidth - leftOffset,
            window.innerHeight - topOffset,
        )
    })

    const tusharRef = React.useRef<(null | HTMLButtonElement)>(null);
    const audioRef = React.useRef<(null | HTMLAudioElement)>(null);
    const [getCookie, ,] = useCookies();
    const [showTutorial, setShowTutorial] = React.useState(!window?.newFlow && !getCookie("showTour"));

    // Theme picker state
    const [isThemePickerOpen, setIsThemePickerOpen] = React.useState(false);
    const hasContentLayer = !!(
        window.flowboard?.content?.presentation ||
        window.flow_json?.flowboard?.content?.presentation
    );
    const currentThemeId = window.flowboard?.content?.themeId ||
        window.flow_json?.flowboard?.content?.themeId || 'modern-light';
    const { recompileWithTheme } = useAIRecompile();
    const handleThemeChange = React.useCallback(async (themeId: string) => {
        await recompileWithTheme(themeId);
    }, [recompileWithTheme]);

    // Recalculate canvas size after mount using actual element heights
    React.useEffect(() => {
        const updateCanvasSize = () => {
            // Wait for elements to be rendered and measured
            const controlPanel = document.getElementById("control_panel");
            const screenSelect = document.getElementById("screen_select");

            if (controlPanel && screenSelect) {
                const newBounds = determineOffsetAndRatios(stage_w, stage_h);
                setStageBounds(newBounds.ratio >= 1 ? { ...newBounds, ratio: 1 / newBounds.ratio } : newBounds);
            }
        };

        // Use setTimeout to ensure DOM is fully rendered
        const timeoutId = setTimeout(updateCanvasSize, 0);
        return () => clearTimeout(timeoutId);
    }, [stage_w, stage_h]);

    // Measure control_panel bottom and screen_select top, set CSS vars for AI panel positioning
    React.useEffect(() => {
        const updateAIPanelPosition = () => {
            const cp = document.getElementById("control_panel");
            const ss = document.getElementById("screen_select");
            if (cp && ss) {
                const cpBottom = cp.getBoundingClientRect().bottom;
                const ssTop = ss.getBoundingClientRect().top;
                document.documentElement.style.setProperty('--ai-panel-top', `${cpBottom + 10}px`);
                document.documentElement.style.setProperty('--ai-panel-bottom', `${window.innerHeight - ssTop + 10}px`);
            }
        };
        // Delay to ensure layout is complete
        const timeoutId = setTimeout(updateAIPanelPosition, 0);
        window.addEventListener('resize', updateAIPanelPosition);
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', updateAIPanelPosition);
        };
    }, []);

    updateListener()
    mountListeners({ stageBoundState: [stageBounds, setStageBounds], audioRef })

    return (
        <React.Fragment>
            <MacAppPopup isOpen={true} />
            {current.value.logic === 'error' && <Popup open={true} closeOnDocumentClick={false} closeOnEscape={false}>
                <p>Oops we ran into an issue.</p></Popup>}
            <TimedPopup trigger={<button className="hidden_button" ref={tusharRef} />} closeIn={1000}>
                <p>For Tushar, our brother, boss, friend and son. We love you.
                    <img src="/include/img/creator/t-smiling.jpg" /></p>
            </TimedPopup>
            <audio preload="none" ref={audioRef}>
                <source src="/include/img/t-prrrrr.mp3" type="audio/mpeg" />
            </audio>
            {showTutorial && (<Suspense fallback={<div></div>}>
                <Tour tutorial={[showTutorial, setShowTutorial]} />
            </Suspense>)}
            <Controls
                setShowTutorial={setShowTutorial}
                size={stageBounds}
                hasContentLayer={hasContentLayer}
                isThemePickerOpen={isThemePickerOpen}
                setIsThemePickerOpen={setIsThemePickerOpen}
                currentThemeId={currentThemeId}
            />
            <div className="background_off_white" style={{ position: 'relative' }}>
                <FlowCanvas
                    size={{
                        width: stageBounds.stage_width,
                        height: stageBounds.stage_height,
                        ratio: stageBounds.ratio
                    }} />
            </div>
            <div className="main">
                <div id="screen_select">
                    <div className="flex">
                        <div className="scroll_flex"
                        >
                            <ScreenSelector />
                        </div>
                        {!window.newFlow && <NewScreen theme_id />}
                    </div>
                </div>
            </div>


            {/* Theme Picker Panel */}
            <EditorThemePicker
                isOpen={isThemePickerOpen}
                onClose={() => setIsThemePickerOpen(false)}
                currentThemeId={currentThemeId}
                onSelectTheme={handleThemeChange}
            />
        </React.Fragment>
    )
}

export default Canvas
